import { type WritableDraft } from 'immer';
import { type OmitIndexSignature } from 'type-fest';

import {
  type CanvasElementFrom,
  type CanvasElementId,
  type CanvasElementType,
  type HeightSettings,
  type PartialPropsOf,
  type WidthSettings,
} from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { type PropertiesConfigInputContainerItemUnifiedActionModel } from '@blaze-shared/elements-domain';

import { type EngineState } from '../../+state/engine';
import { type WorkflowContext } from '../models/ai.model';
import { type CommonFormFieldProps } from '../models/form-field';
import { toWidthSettings, type WidthStyle } from '../models/styles';

import { findElement } from './utils';

export function getInitialProps<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  elementId: CanvasElementId,
  fallback: PartialPropsOf<T>,
): PartialPropsOf<T> {
  const element = findElement<T>(draft, elementId) as { props?: PartialPropsOf<T> } | undefined;
  return element?.props ?? fallback;
}

export function resolveFullWidth(width: WidthStyle | undefined): WidthSettings {
  return width ? toWidthSettings(width) : { mode: 'full' };
}

export function buildCustomActionModel(
  action: { name: string; aiDescription?: string; iconName?: string },
  workflowContext: WorkflowContext | undefined,
): PropertiesConfigInputContainerItemUnifiedActionModel<'custom'> {
  return {
    type: 'custom',
    label: action.name,
    action: workflowContext ? { workflowId: workflowContext.workflow.id } : undefined,
    aiDescription: action.aiDescription,
    icon: action.iconName,
  };
}

/**
 * @param showTitle - passing true or false will generate and apply a title props patch,
 * passing undefined or not passing, when title is empty, a value will not change the props.
 */
export function patchTitle<T extends CanvasElementType>(
  props: PartialPropsOf<T>,
  title: string | undefined,
  showTitle?: boolean,
): PartialPropsOf<T> {
  if (showTitle !== false) {
    if (!title) {
      return props;
    }
    showTitle = true;
  }

  const patch = {
    content: { title: { label: title, enabled: showTitle } },
    dirty: { label: true },
  } as unknown as OmitIndexSignature<PartialPropsOf<T>>;

  return applyPropsPatch(props, patch);
}

export function patchFieldLabel<T extends CanvasElementType>(
  props: PartialPropsOf<T>,
  field: CommonFormFieldProps,
): PartialPropsOf<T> {
  if (field.label) {
    props = patchTitle(props, field.label, field.showLabel);
  }

  if (!field.iconName && !field.tooltip) {
    return props;
  }

  const title: { icon?: string; tooltip?: string } = {
    icon: field.iconName,
    tooltip: field.tooltip,
  };

  return applyPropsPatch(props, {
    content: { title },
  } as unknown as OmitIndexSignature<PartialPropsOf<T>>);
}

export interface FieldSizeOptions {
  /**
   * Leaves the element's registered width in place when `width` is omitted, instead of
   * resolving to full. For fixed-aspect elements where filling the row would move the layout
   * without changing the rendered content.
   */
  keepRegisteredWidthWhenOmitted?: boolean;
  /**
   * Writes a provided `height` as `fixed: true` instead of the default flagless value-mode
   * height, which some size knobs read back as their `min` mode instead of `fixed`.
   */
  fixedHeight?: boolean;
}

export function applyFieldSize<T extends CanvasElementType>(
  element: WritableDraft<CanvasElementFrom<T>>,
  field: Pick<CommonFormFieldProps, 'width' | 'height'>,
  options: FieldSizeOptions = {},
): void {
  const sizable = element as { width: WidthSettings; height?: HeightSettings };

  if (field.width !== undefined || !options.keepRegisteredWidthWhenOmitted) {
    sizable.width = resolveFullWidth(field.width);
  }

  if (field.height !== undefined) {
    sizable.height = options.fixedHeight
      ? { mode: 'value', value: field.height, fixed: true }
      : { mode: 'value', value: field.height };
  }
}
